import React, { useState, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { LightBulbIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import DotenvImportButton from './DotenvImportButton';
import { fusionnerVariables, DotenvEntry } from '../utils/parseDotenv';

let _sensEditorIdCounter = 0;
const nextSensId = () => `svar-${++_sensEditorIdCounter}`;

export interface SensitiveVariable {
  name: string;
  value: string;
}

interface SensitiveVariablesEditorProps {
  variables: SensitiveVariable[];
  onChange: (variables: SensitiveVariable[]) => void;
}

const SensitiveVariablesEditor: React.FC<SensitiveVariablesEditorProps> = ({
  variables,
  onChange
}) => {
  const { t } = (useTranslation as any)('deployments');
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleValues, setVisibleValues] = useState<Record<number, boolean>>({});
  const idsRef = useRef<string[]>([]);

  while (idsRef.current.length < variables.length) {
    idsRef.current.push(nextSensId());
  }
  idsRef.current.length = variables.length;

  const addVariable = () => {
    idsRef.current.push(nextSensId());
    const newVariables = [...variables, { name: '', value: '' }];
    onChange(newVariables);
    setIsExpanded(true);
  };

  const updateVariable = (index: number, field: 'name' | 'value', value: string) => {
    const newVariables = [...variables];
    newVariables[index][field] = value;
    onChange(newVariables);
  };

  const removeVariable = (index: number) => {
    idsRef.current.splice(index, 1);
    const newVariables = variables.filter((_, i) => i !== index);
    onChange(newVariables);
    toast.success('Variable sensible supprimée');
  };

  const toggleValueVisibility = (index: number) => {
    setVisibleValues(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const importerDotenv = (entries: DotenvEntry[]) => {
    const fusion = fusionnerVariables(variables, entries);
    // Les identifiants stables sont indexes : apres une fusion qui retire les
    // lignes vides, les index ont bouge. On les regenere en bloc plutot que de
    // tenter un recalage -- au prix d'un remontage des lignes, invisible ici
    // puisque la liste vient d'etre remplacee.
    idsRef.current = [];
    onChange(fusion.variables);
    setIsExpanded(true);
  };

  const clearAllVariables = () => {
    if (variables.length === 0) return;

    if (window.confirm('Êtes-vous sûr de vouloir supprimer toutes les variables sensibles ?')) {
      onChange([]);
      toast.success('Toutes les variables sensibles ont été supprimées');
    }
  };

  return (
    <div className="bg-surface-2 rounded-card border border-red-500/30 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center">
            <LockClosedIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-text-primary flex items-center">
              Variables Sensibles (Secrets)
              {variables.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-badge text-xs font-medium bg-red-500/12 text-red-400">
                  {variables.length} {variables.length === 1 ? 'secret' : 'secrets'}
                </span>
              )}
            </h3>
            <p className="text-sm text-text-tertiary">
              Stockées de manière sécurisée et chiffrée
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Masquer les secrets' : 'Afficher les secrets'}
          className="text-text-quaternary hover:text-text-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
        >
          <svg
            className={`w-6 h-6 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Info Box */}
      {!isExpanded && variables.length === 0 && (
        <div className="p-4 bg-red-500/12 rounded-md">
          <p className="text-sm text-red-400">
            <LockClosedIcon className="w-4 h-4 inline mr-1" /><strong>Secrets:</strong> Ajoutez ici les informations sensibles (mots de passe, clés API, tokens, URLs de connexion)
          </p>
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && (
        <div className="space-y-4">
          {variables.length > 0 ? (
            <div className="space-y-3">
              {variables.map((variable, index) => (
                <div
                  key={idsRef.current[index]}
                  className="flex items-start space-x-3 p-3 bg-red-500/8 rounded-lg border border-red-500/20"
                >
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      Nom
                    </label>
                    <input
                      type="text"
                      value={variable.name}
                      onChange={(e) => updateVariable(index, 'name', e.target.value)}
                      placeholder="DATABASE_URL"
                      className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-input focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  <div className="flex-1">
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      Valeur (sensible)
                    </label>
                    <div className="relative">
                      <input
                        type={visibleValues[index] ? 'text' : 'password'}
                        value={variable.value}
                        onChange={(e) => updateVariable(index, 'value', e.target.value)}
                        placeholder="••••••••••••"
                        className="w-full px-3 py-2 pr-10 border border-border-1 bg-surface-3 text-text-primary rounded-input focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => toggleValueVisibility(index)}
                        aria-label={visibleValues[index] ? 'Masquer la valeur' : 'Afficher la valeur'}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-quaternary hover:text-text-secondary focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                      >
                        {visibleValues[index] ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-end space-x-1 pt-6">
                    <button
                      type="button"
                      onClick={() => removeVariable(index)}
                      aria-label={`Supprimer le secret ${variable.name || 'sans nom'}`}
                      className="p-2 text-red-400 hover:bg-red-500/12 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 border-2 border-dashed border-red-500/30 rounded-lg">
              <LockClosedIcon className="w-10 h-10 text-text-quaternary mb-3 mx-auto" />
              <h4 className="text-lg font-medium text-text-primary mb-2">
                Aucune variable sensible
              </h4>
              <p className="text-sm text-text-tertiary mb-4">
                Ajoutez des secrets pour votre application
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-border-1">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={addVariable}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-button hover:bg-red-700 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Ajouter un secret
              </button>
              <DotenvImportButton onImport={importerDotenv} contexte="secret" />
            </div>

            {variables.length > 0 && (
              <button
                type="button"
                onClick={clearAllVariables}
                className="text-sm text-red-400 hover:text-red-300 font-medium"
              >
                Tout supprimer
              </button>
            )}
          </div>

          {/* Security Note */}
          <div className="mt-4 p-4 bg-green-500/12 rounded-lg border border-green-500/20">
            <div className="flex items-start">
              <CheckCircleIcon className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" />
              <div className="text-sm text-green-400">
                <p className="font-medium mb-1">Stockage sécurisé</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Les secrets sont stockés de manière sécurisée et chiffrée</li>
                  <li>Ils ne sont jamais exposés dans les logs</li>
                  <li>Accessibles uniquement par votre application</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Common Secrets Examples */}
          <div className="mt-4 p-4 bg-blue-500/12 rounded-lg border border-blue-500/20">
            <div className="flex items-start">
              <LightBulbIcon className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-400">
                <p className="font-medium mb-1">Exemples de secrets courants</p>
                <div className="grid grid-cols-1 gap-2 text-xs mt-2">
                  <code className="bg-blue-500/15 px-2 py-1 rounded block">DATABASE_URL=postgres://user:pass@host:5432/db</code>
                  <code className="bg-blue-500/15 px-2 py-1 rounded block">SECRET_KEY=your-super-secret-key</code>
                  <code className="bg-accent-500/15 px-2 py-1 rounded block">API_KEY=sk-xxxxxxxxxxxx</code>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SensitiveVariablesEditor;
